/**
 * Home page copy.
 *
 * The client is early-stage with no shareable client names and no metrics to
 * claim, so this leads on capability, working method and directness. There is
 * deliberately no counter band and no client logo wall — the technology strip
 * shows tools we build WITH, which is honest and still signals competence.
 *
 * Written for search as well as for readers: the service terms people actually
 * type ("custom web and mobile app development", "AI automation", "QA
 * automation") and the office cities appear in the headings and the opening
 * lines. Keywords earn their place in a sentence that reads naturally or they
 * are left out — no phrase is repeated past the point of sounding human.
 *
 * CALGARY LEADS. Calgary is the primary market and is named first everywhere
 * the two cities appear together; Lahore follows as the second office. Keep
 * that order if this copy is edited.
 */

export const home = {
  hero: {
    /*
      Client-focused rather than brand-focused. "Where Falcon Vision Meets
      Smart Solutions" is the company's own banner line and still carries the
      brand — it now sits in the eyebrow, where it belongs — but a headline
      has one job: say what the visitor gets. This one does.

      Two-tone: white up to `highlightFrom`, accent blue after it, so the
      colour change lands on the promise rather than mid-phrase.
    */
    /* Title Case: the hero renders it as written rather than uppercasing it. */
    headline: 'Custom Software and Automation for Your Next Stage',
    /** The old headline, kept as the brand line above it. */
    eyebrow: 'Software development in Calgary',
    /* No em dash. It is the single most recognisable tell of machine-written
       copy, and two plain sentences say the same thing. */
    subcopy:
      'Web and mobile products, connected business tools, and practical AI automation. Work directly with our engineers from a written scope through launch and support.',
    /*
      One CTA. It opens the booking calendar rather than routing to the form:
      the hero asks for a conversation, and the nav already carries the same
      action plus every other destination.
    */
    primaryCta: { label: 'Schedule a call', href: '/contact' },
    /*
      No capability row under the CTA. It listed "Web & Mobile, AI Automation,
      QA Automation" — the same three things the subcopy names one line above,
      so it repeated the pitch instead of adding to it.
    */
    /*
      No label above the marketplace logos, and deliberately no client count:
      the figure on the site this was modelled on is theirs, and this file's
      whole premise is that nothing unverifiable gets claimed. The logos carry
      the signal on their own.
    */
    /** Baseline strip under the hero. */
    strapline: 'Scoped in writing · Built to hand over · Supported after launch',
  },

  techStrip: {
    eyebrow: 'Technologies we work with',
    /** Two rows scrolling in opposite directions. */
    rowOne: ['React', 'TypeScript', 'Next.js', 'Node.js', 'React Native', 'PostgreSQL', 'Docker', 'Python'],
    rowTwo: ['OpenAI', 'Anthropic', 'n8n', 'Make.com', 'Playwright', 'Pinecone', 'AWS', 'Firebase'],
  },

  positioning: {
    /** Oversized, revealed word-by-word as the user scrolls. */
    statement:
      'Software your team can keep building on. You receive tested code, documented decisions and a practical handover, so the next change does not depend on the original developer.',
    /**
     * Substantiation for the claim, set beside it. Deliberately distinct from
     * home.approach.points, which appear in full further down the page.
     */
    proof: [
      {
        title: 'Typed end to end',
        body: 'TypeScript across client and server, with one shared schema for anything crossing the boundary. A change that breaks something fails at build time, where it is cheap.',
      },
      {
        title: 'Tested where it counts',
        body: 'Playwright suites covering signup, checkout and the other journeys that cost money when they break. They run on every commit and record a video of any failure.',
      },
      {
        title: 'Documented to hand over',
        body: 'A written scope, decisions recorded with the reasoning behind them, and a maintenance runbook. Another team can pick the work up without archaeology.',
      },
    ],
  },

  approach: {
    eyebrow: 'How we work',
    heading: 'Small team, direct line, no account managers',
    body: 'You talk to the people writing the code. Questions get answered the same day, scope changes get discussed rather than invoiced, and nothing is lost in translation through a delivery layer that adds cost without adding judgement.',
    points: [
      {
        title: 'Scope before code',
        body: 'Every engagement starts with a written technical scope. You approve what is being built and what it costs before anyone opens an editor.',
      },
      {
        title: 'Working software, early',
        body: 'You get a deployed, usable version in the first few weeks. Feedback shapes the build while changing course is still cheap.',
      },
      {
        title: 'Built to hand over',
        body: 'Typed code, documented decisions and a maintenance runbook. If you bring the work in-house later, nothing about that should hurt.',
      },
      {
        title: 'Honest about fit',
        body: 'If an off-the-shelf tool solves your problem better than a custom build, we will say so before taking the work.',
      },
    ],
  },

  cta: {
    heading: 'Tell us what is slowing you down',
    body: 'Send a short description of the problem. You will get a real reply from someone technical, usually within one working day.',
    button: { label: 'Get in touch', href: '/contact' },
  },
} as const;
